/**
 * useAIModels Hook - Model catalogue from the database
 * GOVERNANCE: Gemini is the ONLY allowed AI provider
 *
 * The catalogue in `ai_models` is the source of truth: every enabled, active row
 * is selectable in the admin model pickers. The static list in
 * src/config/geminiModels.ts stays as the offline fallback (and for the default
 * model name) so the UI still renders when the catalogue cannot be read.
 */

import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { db } from '@/lib/supabaseQuery';
import {
  GEMINI_MODEL_LIST,
  DEFAULT_GEMINI_MODEL,
  GEMINI_LEGACY_ALIAS_MAP,
} from '@/config/geminiModels';

export interface AIModelOption {
  value: string;
  label: string;
  description: string;
  status: 'active';
  tier: 'standard' | 'premium' | 'lite';
  provider: 'gemini';
  is_enabled: boolean;
  is_default: boolean;
  sort_order: number;
}

export interface AIModelGroup {
  key: string;
  label: string;
  color: string;
  models: AIModelOption[];
}

interface AIModelRow {
  alias: string;
  display_name: string | null;
  description: string | null;
  tier: string | null;
  sort_order: number | null;
}



const FALLBACK_MODELS: AIModelOption[] = GEMINI_MODEL_LIST.map((m, idx) => ({
  value: m.value,
  label: m.label,
  description: m.description,
  status: 'active' as const,
  tier: m.tier,
  provider: 'gemini' as const,
  is_enabled: true,
  is_default: m.isDefault,
  sort_order: idx + 1,
}));

const AI_MODELS_QUERY_KEY: readonly unknown[] = ['ai-models', 'catalogue', 'enabled'];

function normaliseTier(tier: string | null, alias: string): AIModelOption['tier'] {
  if (tier === 'standard' || tier === 'premium' || tier === 'lite') return tier;
  if (alias.includes('lite')) return 'lite';
  if (alias.includes('pro')) return 'premium';
  return 'standard';
}

function toOption(row: AIModelRow, index: number): AIModelOption {
  return {
    value: row.alias,
    label: row.display_name || row.alias,
    description: row.description || '',
    status: 'active',
    tier: normaliseTier(row.tier, row.alias),
    provider: 'gemini',
    is_enabled: true,
    is_default: row.alias === DEFAULT_GEMINI_MODEL,
    sort_order: row.sort_order ?? index + 1,
  };
}

interface UseAIModelsResult {
  groups: AIModelGroup[];
  allModels: AIModelOption[];
  isLoading: boolean;
  error: Error | null;
  defaultModel: string;
  getModelInfo: (alias: string) => AIModelOption | undefined;
  getProviderColor: (alias: string) => string;
  getShortModelName: (alias: string) => string;
  refetch: () => Promise<unknown>;
}

export function useAIModels(): UseAIModelsResult {
  const query = useQuery({
    queryKey: AI_MODELS_QUERY_KEY,
    queryFn: async (): Promise<AIModelOption[]> => {
      const rows = await db.fetchAll<AIModelRow>(
        'ai_models',
        'alias, display_name, description, tier, sort_order',
        {
          filters: [
            { column: 'enabled', operator: 'eq', value: true },
            { column: 'status', operator: 'eq', value: 'active' },
          ],
          orderBy: { column: 'display_name', ascending: true },
        },
      );
      return rows.map(toOption);
    },
    staleTime: 300_000,
  });

  const allModels = useMemo(
    () => (query.data && query.data.length > 0 ? query.data : FALLBACK_MODELS),
    [query.data],
  );

  const groups = useMemo<AIModelGroup[]>(
    () => [
      {
        key: 'gemini',
        label: 'Google Gemini',
        color: 'bg-emerald-500',
        models: allModels,
      },
    ],
    [allModels],
  );

  const getModelInfo = (alias: string): AIModelOption | undefined =>
    allModels.find((m) => m.value === alias);

  const getProviderColor = (alias: string): string => {
    if (alias.startsWith('gemini-')) return 'bg-emerald-500';
    return 'bg-muted';
  };

  const getShortModelName = (alias: string): string => {
    const resolved = GEMINI_LEGACY_ALIAS_MAP[alias] ?? alias;
    return getModelInfo(resolved)?.label || alias;
  };

  return {
    groups,
    allModels,
    isLoading: query.isLoading,
    error: (query.error as Error | null) ?? null,
    defaultModel: DEFAULT_GEMINI_MODEL,
    getModelInfo,
    getProviderColor,
    getShortModelName,
    refetch: query.refetch,
  };
}
